import React, { useState } from "react";
import { Typography, Box, useTheme, Skeleton } from "@mui/material";
import ReactApexChart from "react-apexcharts";
import { useGetRiskHeatMatrixQuery } from "../../../store/slices/apis/riskDashboardSlice";
import { useParams } from "react-router-dom";
import RiskPopup from "./RiskPopup";

const RiskHeatMatrix = () => {
  const { projectId } = useParams();
  const [popupData, setPopupData] = useState(null);
  const [open, setOpen] = useState(false);
  const theme = useTheme();

  const {
    data: RiskHeatMatrix = [],
    error,
    isLoading,
  } = useGetRiskHeatMatrixQuery({
    project_id: projectId,
  });

  const yAxisName = ["Negligible", "Low", "Moderate", "High", "Extreme"];
  const xAxisName = ["Negligible", "Low", "Moderate", "High", "Extreme"];

  const colorMatrix = [
    [1, 2, 3, 4, 5],
    [2, 4, 6, 8, 10],
    [3, 6, 9, 12, 15],
    [4, 8, 12, 16, 20],
    [5, 10, 15, 20, 25],
  ];

  const createInitialHeatmapData = () => {
    return colorMatrix.map((row, rowIndex) => ({
      name: yAxisName[rowIndex],
      data: row.map((values, colIndex) => ({
        x: xAxisName[colIndex],
        y: values,
        customValues: 0,
      })),
    }));
  };

  const updateHeatmapData = (heatmapData, apiData) => {
    const updated = heatmapData.map((row) => ({
      ...row,
      data: row.data.map((cell) => ({ ...cell })),
    }));

    apiData?.forEach(({ likelihoodScore, impactScoreOverall, count }) => {
      const row = likelihoodScore - 1;
      const col = impactScoreOverall - 1;
      if (updated[row] && updated[row].data[col]) {
        updated[row].data[col].customValues = count;
      }
    });

    return updated;
  };

  const initialData = createInitialHeatmapData();
  const series = updateHeatmapData(initialData, RiskHeatMatrix?.data);

  const options = {
    chart: {
      type: "heatmap",
      toolbar: { show: false },
      offsetY: -20,
      offsetX: 0,
      animations: { enabled: false },
      events: {
        dataPointSelection: (event, chartContext, config) => {
          const { seriesIndex, dataPointIndex } = config;
          const point = series[seriesIndex].data[dataPointIndex];

          if (!point.customValues || point.customValues === 0) return;

          const xIndex = xAxisName.indexOf(point.x) + 1;
          const yIndex = yAxisName.indexOf(series[seriesIndex].name) + 1;

          setPopupData({
            seriesName: series[seriesIndex].name,
            x: point.x,
            y: point.y,
            seriesValue: point.customValues,
            score: `${yIndex},${xIndex}`,
          });
          setOpen(true);
        },
      },
    },
    plotOptions: {
      heatmap: {
        distributed: true,
        enableShades: false,
        colorScale: {
          ranges: [
            { from: 0, to: 2, color: "#006045" },
            { from: 3, to: 4, color: "#8EC24C" },
            { from: 5, to: 9, color: "#F0BD39" },
            { from: 10, to: 16, color: "#EE4F3E" },
            { from: 17, to: 25, color: "#841F13" },
          ],
        },
      },
    },
    dataLabels: {
      enabled: true,
      formatter: function (val, opts) {
        const { seriesIndex, dataPointIndex, w } = opts;
        const customLabel =
          w.globals.initialSeries[seriesIndex].data[dataPointIndex]
            .customValues;
        return customLabel || 0;
      },
      style: {
        colors: ["#FFFFFF"],
        fontWeight: "400",
        fontSize: "12px",
        lineHeight: "20px",
      },
    },
    tooltip: {
      y: {
        formatter: function (val, opts) {
          const { seriesIndex, dataPointIndex, w } = opts;
          const customLabel =
            w.globals.initialSeries[seriesIndex].data[dataPointIndex]
              .customValues;
          return customLabel || 0;
        },
      },
    },

    xaxis: {
      title: {
        text: "Impact Score",
        style: {
          fontWeight: 600,
          fontSize: "14px",
          color: "#333",
        },
      },
      lineWidth: 1,
      lineColor: "#758195",
      labels: {
        style: {
          fontWeight: 500,
          fontSize: "12px",
          colors: "#B4B4B4",
          lineHeight: "16px",
        },
        offsetY: -2,
      },
    },
    yaxis: {
      title: {
        text: "Likelihood ",
        style: {
          fontWeight: 600,
          fontSize: "14px",
          color: "#333",
        },
      },
      lineWidth: 1,
      lineColor: "#758195",
      labels: {
        style: {
          fontWeight: 500,
          fontSize: "12px",
          colors: "#B4B4B4",
          lineHeight: "20px",
        },
      },
    },
    legend: {
      show: true,
      position: "bottom",
      horizontalAlign: "center",
      markers: {
        width: 11,
        height: 11,
        radius: 2,
      },
      labels: {
        colors: "#333",
        useSeriesColors: false,
      },
      itemMargin: {
        horizontal: 10,
        vertical: 4,
      },
      formatter: function (seriesName, opts) {
        const customLabels = [
          "Very Low",
          "Planned Progress",
          "Medium",
          "High",
          "Very High",
        ];
        return customLabels[opts.seriesIndex] || seriesName;
      },
    },

    title: { text: "" },
  };

  const bottomLegend = [
    { label: "Very Low", color: "#006045" },
    { label: "Planned Progress", color: "#8EC24C" },
    { label: "Medium", color: "#F0BD39" },
    { label: "High", color: "#EE4F3E" },
    { label: "Very High", color: "#841F13" },
  ];

  if (isLoading) {
    return (
      <Box
        sx={{
          mt: 2,
          backgroundColor: "white",
          borderRadius: "5px",
          padding: "20px",
        }}
      >
        <Skeleton variant="text" width="180px" height={30} sx={{ mb: 2 }} />
        <Skeleton variant="rectangular" height={300} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: "100%",
        height: "424px",
        border: "1px solid #D8D8D8",
        mt: 0,
        borderRadius: "12px",
        overflow: "hidden",
        backgroundColor: "white",
      }}
    >
      <Box sx={{ p: 2 }}>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 500,
            fontSize: "18px",
            lineHeight: "100%",
            letterSpacing: "-0.4px",
            color: `${theme?.customSettings?.themeColor}`,
          }}
        >
          Risk Rating
        </Typography>
        <Typography
          sx={{
            mt: "8px",
            color: "#5052588A",
            fontSize: "14px",
            lineHeight: "100%",
            letterSpacing: "-0.4px",
          }}
        >
          Risk likelihood and impact to prioritize mitigation strategies effectively
        </Typography>
      </Box>

      {/* Heatmap */}
      <ReactApexChart
        options={options}
        series={series}
        type="heatmap"
        height="330"
        style={{ cursor: "pointer" }}
      />

      {/* Popup */}
      {open && (
        <RiskPopup
          title={"Risk Heat Matrix"}
          value={popupData.score}
          status={"HeatMatrix"}
          onOpen={open}
          onClose={() => setOpen(false)}
        />
      )}
    </Box>
  );
};

export default RiskHeatMatrix;